<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { BaseAvatar, BaseButton } from '@magma-app/magma-lapilli';

const route = useRoute();
const root = ref<HTMLElement>();
const english = computed(() => route.query.lang === 'en');
const copy = computed(() => english.value ? {
  tagline: 'Turn your ambassadors into growth drivers.',
  title: 'This ambassador program has ended.',
  description: 'New connections are always possible. Magma helps organizations bring their communities together through their ambassadors.',
  action: 'Discover Magma', label: 'The ambassador platform',
} : {
  tagline: 'Transformez vos ambassadeurs en leviers de croissance.',
  title: 'Ce programme ambassadeur est arrêté.',
  description: 'Les rencontres continuent avec Magma. Nous aidons les organisations à faire vivre leur communauté grâce à leurs ambassadeurs.',
  action: 'Découvrir Magma', label: 'La plateforme des ambassadeurs',
});

const discoverMagma = () => {
  window.open('https://magma.app?utm_source=closed_program&utm_medium=embed', '_blank', 'noopener,noreferrer');
};

let observer: ResizeObserver | undefined;
const notifyParent = () => {
  if (window.parent === window || !document.referrer) return;
  try {
    // The parent may resize the iframe; the promotional screen stays visible by default.
    window.parent.postMessage({ type: 'magma:program-ended', height: root.value?.scrollHeight || 560 },
      new URL(document.referrer).origin);
  } catch { /* A missing parent origin requires no reporting. */ }
};
onMounted(() => {
  notifyParent();
  observer = new ResizeObserver(notifyParent);
  if (root.value) observer.observe(root.value);
});
onUnmounted(() => observer?.disconnect());
</script>

<template>
  <main
    ref="root"
    class="grid min-h-[560px] place-items-center bg-background-brand-default px-6 py-14 text-center text-foreground-emphasis"
    :lang="english ? 'en' : 'fr'"
  >
    <div class="w-full max-w-2xl">
      <a
        class="inline-flex items-center gap-4 rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-border-brand-emphasis"
        href="https://magma.app"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Magma"
      >
        <BaseAvatar src="/img/logo_magma.png" alt="" size="xl" shape="rounded" />
        <span class="text-mgm-dspl-xl font-semibold tracking-tight text-foreground-brand-default">magma</span>
      </a>
      <p class="mb-6 mt-4 text-mgm-txt-sm font-medium text-foreground-brand-default">
        {{ copy.label }}
      </p>
      <h1 class="text-mgm-dspl-sm font-semibold tracking-tight sm:text-mgm-dspl-md">
        {{ copy.tagline }}
      </h1>
      <div class="mx-auto mb-7 mt-8 max-w-lg">
        <h2 class="mb-3 text-mgm-txt-lg font-semibold">{{ copy.title }}</h2>
        <p class="text-mgm-txt-md text-foreground-default">{{ copy.description }}</p>
      </div>
      <BaseButton
        class="mx-auto"
        :text="copy.action"
        size="xl"
        state="primary"
        type="button"
        icon="arrow-up-right"
        icon-position="right"
        @click="discoverMagma"
      />
      <p class="mt-4 text-mgm-txt-sm text-foreground-subtle">magma.app</p>
    </div>
  </main>
</template>
